import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useWalletSafe } from "@/hooks/useWalletSafe";

// System 1: Image/Copyright strike system (user_strikes table)
export type StrikeStatus = {
  strike_count: number;
  is_banned: boolean;
  ban_expires_at: string | null;
  last_strike_at: string | null;
  last_strike_reason: string | null;
  ban_reason: string | null;
};

// System 2: Anti-fraud system (fraud_identities table)
export type FraudStatus = {
  fraud_identity_id: string | null;
  status: 'clean' | 'warned' | 'restricted' | 'banned';
  strikes: number;
  risk_score: number;
  is_banned: boolean;
};

// Combined status for UI
export type CombinedBanStatus = {
  // From user_strikes (copyright/image)
  imageStrikes: StrikeStatus;
  // From fraud_identities (anti-fraud)
  fraudStatus: FraudStatus;
  // Combined flags for easy UI checks
  isAnyBanned: boolean;
  isImageBanned: boolean;
  isFraudBanned: boolean;
  isFraudRestricted: boolean;
  showFraudWarning: boolean;
  totalStrikes: number;
};

export function useStrikeStatus() {
  const { publicKey } = useWalletSafe();
  const [strikeStatus, setStrikeStatus] = useState<StrikeStatus | null>(null);
  const [fraudStatus, setFraudStatus] = useState<FraudStatus | null>(null);
  const [combinedStatus, setCombinedStatus] = useState<CombinedBanStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const walletAddress = publicKey?.toBase58() || null;

  const fetchStrikeStatus = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // ===== SYSTEM 1: Image/Copyright Strikes =====
      let imageStrikeData: StrikeStatus = {
        strike_count: 0,
        is_banned: false,
        ban_expires_at: null,
        last_strike_at: null,
        last_strike_reason: null,
        ban_reason: null,
      };

      if (user) {
        const { data, error } = await supabase
          .from("user_strikes")
          .select("strike_count, is_banned, ban_expires_at, last_strike_at, last_strike_reason, ban_reason")
          .eq("user_id", user.id)
          .maybeSingle();

        if (!error && data) {
          const banExpired = !!(data.is_banned && data.ban_expires_at && new Date(data.ban_expires_at) < new Date());

          // If the ban is expired, call the backend helper to auto-unban and keep DB/UI consistent.
          if (banExpired) {
            await supabase.rpc('is_user_banned', { p_user_id: user.id });
          }

          imageStrikeData = {
            ...data,
            is_banned: banExpired ? false : data.is_banned,
            ban_expires_at: banExpired ? null : data.ban_expires_at,
            ban_reason: data.ban_reason || null,
          };
        }
      }

      setStrikeStatus(imageStrikeData);

      // ===== SYSTEM 2: Anti-Fraud Status =====
      let fraudStatusData: FraudStatus = {
        fraud_identity_id: null,
        status: 'clean',
        strikes: 0,
        risk_score: 0,
        is_banned: false,
      };

      if (walletAddress) {
        const { data: fraudData, error: fraudError } = await supabase
          .rpc('get_wallet_fraud_status', { p_wallet_address: walletAddress });

        if (!fraudError && fraudData && fraudData.length > 0) {
          const fd = fraudData[0];
          fraudStatusData = {
            fraud_identity_id: fd.fraud_identity_id,
            status: fd.status,
            strikes: fd.strikes,
            risk_score: fd.risk_score,
            is_banned: fd.is_banned,
          };
        }
      }

      setFraudStatus(fraudStatusData);

      // ===== COMBINED STATUS =====
      const isImageBanned = imageStrikeData.is_banned;
      const isFraudBanned = fraudStatusData.status === 'banned';
      const isFraudRestricted = fraudStatusData.status === 'restricted';
      const showFraudWarning = fraudStatusData.status === 'restricted' || fraudStatusData.status === 'warned';

      setCombinedStatus({
        imageStrikes: imageStrikeData,
        fraudStatus: fraudStatusData,
        isAnyBanned: isImageBanned || isFraudBanned,
        isImageBanned,
        isFraudBanned,
        isFraudRestricted,
        showFraudWarning,
        totalStrikes: imageStrikeData.strike_count + fraudStatusData.strikes,
      });

    } catch (err) {
      console.error("Strike/Fraud status error:", err);
      setStrikeStatus(null);
      setFraudStatus(null);
      setCombinedStatus(null);
    }
    setLoading(false);
  }, [walletAddress]);

  useEffect(() => {
    fetchStrikeStatus();
  }, [fetchStrikeStatus]);

  // Subscribe to realtime changes for user_strikes
  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setupSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      channel = supabase
        .channel('strike-fraud-changes')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'user_strikes',
          filter: `user_id=eq.${user.id}`
        }, () => {
          fetchStrikeStatus();
        })
        .subscribe();
    };

    setupSubscription();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [fetchStrikeStatus]);

  // Subscribe to fraud_identities changes via wallet_links
  useEffect(() => {
    if (!walletAddress) return;

    const fraudChannel = supabase
      .channel('fraud-identity-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'fraud_wallet_links',
        filter: `wallet_address=eq.${walletAddress}`
      }, () => {
        fetchStrikeStatus();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(fraudChannel);
    };
  }, [walletAddress, fetchStrikeStatus]);

  return { 
    // Legacy compatibility
    strikeStatus, 
    // New fraud status
    fraudStatus,
    // Combined for easy checks
    combinedStatus,
    loading, 
    refetch: fetchStrikeStatus 
  };
}
